/**
 * AppWrapper component to provide routing functions to HeroUIProvider
 *
 * This enables HeroUI components (Tabs, Dropdown, Button, etc.) to use React Router
 * for client-side navigation without page reloads
 *
 * Architecture:
 * - Shared UI providers (HeroUIProvider, ToastProvider) mount for all users
 * - PrivateAppProvider conditionally mounts only for authenticated users
 * - This prevents auth-only services from executing on public pages (login, signup)
 */

import { useNavigate, useHref } from 'react-router-dom';
import { HeroUIProvider } from '@heroui/react';
import { ToastProvider } from '@enfomentis/ui';
import { AuthProvider } from '@/providers/AuthProvider';
import { PrivateAppProvider } from '@/providers/PrivateAppProvider';
import { useAuth } from '@/hooks/useAuth';
import App from './App';

/**
 * Inner wrapper component that uses auth context
 * This must be inside AuthProvider to access useAuth()
 *
 * Provides shared UI framework (HeroUIProvider, ToastProvider) for all users.
 * Conditionally wraps authenticated-only services in PrivateAppProvider.
 */
function AppContent() {
  const navigate = useNavigate();
  const { user } = useAuth();

  return (
    <HeroUIProvider navigate={navigate} useHref={useHref}>
      <ToastProvider>
        {user ? (
          <PrivateAppProvider key={user.id} user={user}>
            <App />
          </PrivateAppProvider>
        ) : (
          <App />
        )}
      </ToastProvider>
    </HeroUIProvider>
  );
}

/**
 * Outer wrapper component that provides authentication context
 * Wraps the entire app with AuthProvider
 */
export function AppWrapper() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}